import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { getProductId, getEnvironmentId, getProductName } from "utils/localStorage";
import { CheckCircle, ArrowRight, ArrowLeft, ChevronDown, ChevronRight, TrendingUp, X, HelpCircle, ExternalLink } from 'lucide-react';
import ChargebeeIcon from '../../../assets/images/Chargebee_ideEpxhfdE_1.svg';
import { TipsSection } from '../Common/TipsSection';
import { Radio } from 'antd';
import { useGetPlanDataLazyQuery, useGetCustomerRawDataLazyQuery,useGetTransactionStatusLazyQuery } from '../../../store/graphql/codegen/auto-gen/revenue-report'
import { revenueClient } from '../../../store/graphql/apollo/clients'
import { FeatureUnlockCelebration } from '../Common/FeatureUnlockCelebration';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/redux/store'
import { useOnboardingContext } from '../../../container/Layout/OnboardingContext/OnboardingContext';
import { useDemoMode } from '../../../context/DemoModeContext';
import { mrrConfigQuestions } from './utils/utils';

interface SetupMRRProps {
  includeTransactionFees: boolean;
  setIncludeTransactionFees: (value: boolean) => void;
  step2UploadedFiles: (File | null)[];
  step2CsvHeaders: string[][];
  step2SampleRows: Record<string, string[][]>;
  onComplete: (selectedPlans: string[], selectedStatuses: string[], pastDueHandling: boolean, includeTax: boolean, baseCurrency: string, includeTransactionFees: boolean) => void;
  loadedBaseCurrency?: string;
  loadedMrrPlans?: string[];
  loadedMrrStatuses?: string[];
  loadedPastDueHandling?: boolean;
  onGoToDataSetup?: () => void;
  statusOptions?: string[];
  setCsvDetectedStatuses: (value: string[]) => void
  dataSetupIsComplete?: boolean;
  selectedDataType: string | null;
  setIsMrrCompleted: (value: boolean) => void
  isMrrCompleted: boolean
  includeTax: boolean
  onStepComplete: (value: boolean, overrideValues?: {
    selectedMrrPlans: string[];
    selectedMrrStatuses: string[];
    selectedPastDueHandling: boolean;
    includeTax: boolean;
    baseCurrency: string;
    includeTransactionFees: boolean;
  }) => void;
  selectedSources:string;
  chargebeeFeePerTransaction?: number;
  setChargebeeFeePerTransaction?: (value: number) => void;
  paymentGatewayFees?: number;
  setPaymentGatewayFees?: (value: number) => void;
  paymentGatewayType?: string;
  setPaymentGatewayType?: (value: string) => void;
  selectedPaymentGateway?: string;
  setSelectedPaymentGateway?: (value: string) => void;
  chargebeeChargesPerTxnFee?: boolean;
  setChargebeeChargesPerTxnFee?: (value: boolean) => void;
  activeSourceTab: string;
}

enum QuestionType {
  Checkbox = 'checkbox',
  Dropdown = 'dropdown',
  Radio = 'radio',
  SingleCheckbox = 'single-checkbox',
}

interface QuestionConfig {
  id: string;
  number: number;
  title: string;
  description: string;
  type: QuestionType;
  options: string[];
  defaultValue?: string | string[] | boolean;
  defaultText?: string;
  gridCols?: number;
  required?: boolean;
  checkboxLabel?: string;
  example?: string;
}

interface MetricConfig {
  id: string;
  label: string;
  labelBgColor: string;
  labelTextColor: string;
  title: string;
  description: string;
  isCompleted: boolean;
  isActive: boolean;
  questions: QuestionConfig[];
  tip?: {
    title: string;
    content: React.ReactNode;
    icon?: React.ReactNode;
    type?: "success" | "info";
    timeout?: number;
  };
}

const currencySymbolMap: Record<string, string> = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  CAD: "$",
  AUD: "$",
  JPY: "¥",
  CHF: "₣",
  CNY: "¥",
  INR: "₹",
  BRL: "R$",
  KRW: "₩",
  SGD: "$",
  HKD: "$",
  NZD: "$",
  SEK: "kr",
  NOK: "kr",
  DKK: "kr",
  PLN: "zł",
  CZK: "Kč",
  HUF: "Ft"
};


export const SetupMRR: React.FC<SetupMRRProps> = ({
  step2UploadedFiles,
  step2CsvHeaders,
  step2SampleRows,
  onComplete,
  loadedMrrPlans,
  loadedMrrStatuses,
  loadedPastDueHandling,
  onGoToDataSetup,
  statusOptions,
  dataSetupIsComplete = false,
  selectedDataType,
  setIsMrrCompleted,
  setCsvDetectedStatuses,
  includeTax,
  onStepComplete,
  loadedBaseCurrency,
  includeTransactionFees,
  setIncludeTransactionFees,
  selectedSources,
  chargebeeFeePerTransaction = 0,
  setChargebeeFeePerTransaction,
  paymentGatewayFees = 0,
  setPaymentGatewayFees,
  paymentGatewayType = '',
  setPaymentGatewayType,
  selectedPaymentGateway = '',
  setSelectedPaymentGateway,
  chargebeeChargesPerTxnFee: chargebeeChargesPerTxnFeeProp,
  setChargebeeChargesPerTxnFee: setChargebeeChargesPerTxnFeeProp,
  activeSourceTab: activeSourceTab
}) => {
  const [selectedMrrPlans, setSelectedMrrPlans] = useState<string[]>([]);
  const [selectedMrrStatuses, setSelectedMrrStatuses] = useState<string[]>([]);
  const [activeMetric, setActiveMetric] = useState<string>('mrr');
  const [continuedMetrics, setContinuedMetrics] = useState<Set<string>>(new Set());
  const [pastDueHandling, setPastDueHandling] = useState<boolean>(true);
  const [baseCurrency, setBaseCurrency] = useState<string>('USD');

  const [apiPlanOptions, setApiPlanOptions] = useState<string[]>([]);
  const [apiStatusOptions, setApiStatusOptions] = useState<string[]>([]);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(true);
  const [hasDataSetupError, setHasDataSetupError] = useState<boolean>(false);
  const [includeTaxes, setIncludeTax] = useState<boolean>(false);
  const [showCelebration, setShowCelebration] = useState<boolean>(false);
  const { isReady } = useOnboardingContext();
  const [chargebeeChargesPerTxnFeeInternal, setChargebeeChargesPerTxnFeeInternal] = useState<boolean>(
    chargebeeFeePerTransaction > 0
  );
  const chargebeeChargesPerTxnFee = chargebeeChargesPerTxnFeeProp ?? chargebeeChargesPerTxnFeeInternal;
  const setChargebeeChargesPerTxnFee = setChargebeeChargesPerTxnFeeProp ?? setChargebeeChargesPerTxnFeeInternal;
  const [isTransactionFeesDrawerOpen, setIsTransactionFeesDrawerOpen] = useState<boolean>(false);
  const { isDemoMode } = useDemoMode();

  const initializedRef = useRef(false);

  const currencyOptions = [
    'USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CHF', 'CNY', 'INR', 'BRL',
    'KRW', 'SGD', 'HKD', 'NZD', 'SEK', 'NOK', 'DKK', 'PLN', 'CZK', 'HUF'
  ];

  const [getPlanData] = useGetPlanDataLazyQuery({
    client: revenueClient,
    fetchPolicy: "no-cache"
  })

  const [getTransactionStatus] = useGetTransactionStatusLazyQuery({
      client: revenueClient,
      fetchPolicy: "no-cache"
  })

  const revenueAnalyticsData = useSelector((state: RootState) => {
    if (!isReady) return null;

    const revenueAnalyticsModule = state.modules.find(module => module.moduleId === 'revenue_analytics');
    const dataSetupStep = revenueAnalyticsModule?.stepDetails.find(step => step.stepId === 'data_setup');
    if (!dataSetupStep?.value) return null;
    
    try {
      const parsedValue = JSON.parse(dataSetupStep.value);
      const transactionsSubStep = parsedValue.subStepsData?.find((subStep: any) => subStep.key === 'transactions');
      
      if (!transactionsSubStep) return null;
      
      return {
        fieldMapping: transactionsSubStep.fieldMapping,
        taxAmountMapping: transactionsSubStep.fieldMapping?.['Tax Amount'] || null,
        currecy: transactionsSubStep.fieldMapping?.['Transaction Fee'] || null,
        transactionFees: transactionsSubStep.fieldMapping?.['Currency'] || null,
        transactionsData: transactionsSubStep,
        hasTransactionData: !!transactionsSubStep
      };
    } catch (error) {
      console.error('Error parsing data_setup value:', error);
      return null;
    }
  });

  useEffect(() => {
    if (isDemoMode) {
      setIsLoadingData(false);
      setHasDataSetupError(false);
      setApiPlanOptions(['Basic', 'Pro', 'Enterprise']);
      setApiStatusOptions(['paid', 'success', 'pending', 'failed']);
      return;
    }
    if (!isReady) return;
    const loadApiData = () => {
      setIsLoadingData(true);
      setHasDataSetupError(false);

      getTransactionStatus({
        variables: {
          input: {
            productId: getProductId() ?? "",
            environmentId: getEnvironmentId() ?? ""
          }
        },
        onCompleted: (data: any) => {
          setCsvDetectedStatuses(data.getTransactionStatus.status)
        }
      })

      getPlanData({
        variables: {
          input: {
            productId: getProductId() ?? "",
            environmentId: getEnvironmentId() ?? "",
          }
        },
        onCompleted: (planResult) => {
          const planData = planResult?.getPlanData;

          if (!planData || !planData.data || planData.data.length === 0) {
            setHasDataSetupError(true);
            setIsLoadingData(false);
            return;
          }

          const planOptions = planData.data.map((plan: any) => plan.planName).filter(Boolean);
          setApiPlanOptions(planOptions);

          setIsLoadingData(false);
        },
        onError: (error) => {
          console.error('Error loading plan data:', error);
          setHasDataSetupError(true);
          setIsLoadingData(false);
        }
      });
    };

    loadApiData();
  }, [isReady, isDemoMode]);

  useEffect(() => {
    if (selectedMrrStatuses.length > 0 || (loadedMrrStatuses && loadedMrrStatuses.length > 0)) {
      return;
    }

    const availableOptions = (statusOptions && statusOptions.length > 0)
      ? statusOptions
      : apiStatusOptions;

    if (availableOptions.length > 0) {
      const defaultStatuses = ['paid', 'success'].filter(status =>
        availableOptions.includes(status)
      );

      if (defaultStatuses.length > 0) {
        setSelectedMrrStatuses(defaultStatuses);
      }
    }
  }, [statusOptions, apiStatusOptions, selectedMrrStatuses, loadedMrrStatuses]);


  useEffect(() => {
    if (continuedMetrics.has('mrr')) {
      onComplete(selectedMrrPlans, selectedMrrStatuses, pastDueHandling, includeTaxes, baseCurrency, includeTransactionFees);
      onStepComplete(false, {
        selectedMrrPlans,
        selectedMrrStatuses,
        selectedPastDueHandling: pastDueHandling,
        includeTax: includeTaxes,
        baseCurrency,
        includeTransactionFees: includeTransactionFees
      });
    }
  }, [continuedMetrics]);

  useEffect(() => {
    if (initializedRef.current) return;

    if (loadedMrrPlans && loadedMrrPlans?.length > 0) {
      setSelectedMrrPlans(loadedMrrPlans);
    }

    if (loadedMrrStatuses && loadedMrrStatuses?.length > 0) {
      setSelectedMrrStatuses(loadedMrrStatuses);
    }
    if (loadedPastDueHandling !== undefined) {
      setPastDueHandling(loadedPastDueHandling);
    }
    if (includeTax !== undefined) {
      setIncludeTax(includeTax);
    }
    if (loadedBaseCurrency) {
      setBaseCurrency(loadedBaseCurrency);
    }
    if (includeTransactionFees != undefined) {
      setIncludeTransactionFees(includeTransactionFees);
    }

    initializedRef.current = true;
  }, [loadedMrrPlans, loadedMrrStatuses, loadedPastDueHandling, includeTax, loadedBaseCurrency]);


  const subscriptionQuestions: QuestionConfig[] = React.useMemo(() => {

    const baseQuestions = [
      {
          id: mrrConfigQuestions.PLANS,
          number: 1,
          title: 'Plans to Include in MRR',
          description: 'Choose which subscription plans should be included in MRR calculations',
          type: QuestionType.Checkbox,
          options: [],
          gridCols: 3,
          required: true
        },
      {
        id: mrrConfigQuestions.BASE_CURRECY,
        number: 2,
        title: 'Select Base Currency',
        description: `All monetary values in the data will be converted to the base currency. We will convert based on the exchange rate applicable on that date.`,
        type: QuestionType.Dropdown,
        options: currencyOptions,
        defaultValue: 'USD',
        required: true
      },
      {
        id: mrrConfigQuestions.INCLUDE_TAX,
        number: 3,
        title: 'Exclude Tax in MRR Calculations?',
        description: `We detected that you have mapped a tax column (${revenueAnalyticsData?.taxAmountMapping}), which is why we are excluding it by default. Please check this option if you want to exclude tax amounts from your calculations.`,
        checkboxLabel: 'Exclude tax amounts from the monthly recurring revenue calculation',
        type: QuestionType.SingleCheckbox,
        options: [],
        defaultValue: true
      },
      {
        id: mrrConfigQuestions.TRANSACTION_FEES,
        number: 4,
        title: 'Exclude Transaction Fees in MRR Calculations?',
        description: activeSourceTab === 'chargebee'
          ? 'Check this option if you want to exclude transaction fees (Chargebee fees + payment gateway fees) from your MRR calculations. You will be able to configure the fee percentages in the next step.'
          : `We detected that you have mapped a transaction fee column (${revenueAnalyticsData?.transactionFees}), which is why we are excluding it by default. Please check this option if you want to exclude transaction fees from your calculations.`,
        checkboxLabel: 'Exclude transaction fees from the monthly recurring revenue calculation',
        type: QuestionType.SingleCheckbox,
        options: [],
        defaultValue: true
      },
      {
        id: mrrConfigQuestions.PAST_DUE_HANDLING,
        number: 5,
        title: 'Handling Past-due Subscriptions',
        description: 'Configure how past-due subscriptions should be handled',
        checkboxLabel: 'Past-due subscriptions count towards MRR until cancelled',
        type: QuestionType.SingleCheckbox,
        options: [],
        defaultValue: true
      }
    ];

    // Add Chargebee-specific fee configuration question
    const chargebeeFeeQuestion: QuestionConfig = {
      id: mrrConfigQuestions.CHARGEBEE_FEES,
      number: 6,
      title: 'Configure Transaction Fees',
      description: 'Specify the transaction fees for your Chargebee integration. These fees will be excluded from your MRR calculations.',
      type: QuestionType.Checkbox, // Will be handled specially in renderQuestionContent
      options: [],
      checkboxLabel: '', // Not used, handled custom
      defaultValue: false
    };

    // For Chargebee/QuickBooks: show base questions only (no Payment Status question)
    // Fee configuration inputs will appear within TRANSACTION_FEES question when checkbox is checked
    if (activeSourceTab === 'chargebee' || activeSourceTab === 'quickbooks') {
      return baseQuestions.map((q, idx) => ({ ...q, number: idx + 1 }));
    }

    // For other non-Stripe sources (CSV, API, etc.): show Payment Status + base questions (no fee question)
    if (activeSourceTab !== 'stripe') {
      return [
        {
        id: mrrConfigQuestions.PAYMENT_STATUS,
        number: 1,
        title: 'Payment Status for Revenue Recognition',
        description: 'Select which payment statuses should be considered for revenue calculation',
        type: QuestionType.Checkbox,
        options: [],
        gridCols: 2,
        defaultText: 'Default: Only "paid" or "success" status is included'
      },
        ...baseQuestions.map((q, idx) => ({ ...q, number: idx + 2 }))
      ];
    }

    // For Stripe: show only base questions
    return baseQuestions;

  }, [activeSourceTab, revenueAnalyticsData, includeTransactionFees]);


  const renderQuestionContent = (question: QuestionConfig) => {
    const getOptions = () => {
      switch (question.id) {
        case mrrConfigQuestions.PLANS:
          return apiPlanOptions;
        case mrrConfigQuestions.PAYMENT_STATUS:
          return (statusOptions && statusOptions.length > 0)
            ? statusOptions
            : apiStatusOptions;
        default:
          return question.options;
      }
    };

    const getValue = () => {
      switch (question.id) {
        case mrrConfigQuestions.PLANS:
          return selectedMrrPlans;
        case mrrConfigQuestions.PAYMENT_STATUS:
          return selectedMrrStatuses;
        case mrrConfigQuestions.BASE_CURRECY:
          return baseCurrency;
        case mrrConfigQuestions.PAST_DUE_HANDLING:
          return pastDueHandling;
        case mrrConfigQuestions.INCLUDE_TAX:
          return !includeTaxes;
        case mrrConfigQuestions.TRANSACTION_FEES:
          return !includeTransactionFees;
        default:
          return question.defaultValue || [];
      }
    };

    const handleChange = (value: string | string[] | boolean) => {
      switch (question.id) {
        case mrrConfigQuestions.PLANS:
          setSelectedMrrPlans(Array.isArray(value) ? value as string[] : [value as string]);
          break;
        case mrrConfigQuestions.PAYMENT_STATUS:
          setSelectedMrrStatuses(Array.isArray(value) ? value as string[] : [value as string]);
          break;
        case mrrConfigQuestions.BASE_CURRECY:
          setBaseCurrency(value as string);
          break;
        case mrrConfigQuestions.PAST_DUE_HANDLING:
          setPastDueHandling(value as boolean);
          break;
        case mrrConfigQuestions.INCLUDE_TAX:
          setIncludeTax(!Boolean(value));
          break;
        case mrrConfigQuestions.TRANSACTION_FEES:
          setIncludeTransactionFees(!Boolean(value));
          break;
      }
    };

    return (
      <div className="mb-8">
        <div className="font-semibold text-gray-900 mb-2">{question.number}. {question.title}</div>
        <p className="text-sm text-gray-600 mb-4 ml-5">{question.description}</p>

        {question.type === 'checkbox' && (
          <div className={`ml-5 grid grid-cols-1 md:grid-cols-${question.gridCols || 3} gap-3`}>
            {getOptions().map(option => (
              <label key={option} className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer rounded-lg">
                <input
                  type="checkbox"
                  checked={Array.isArray(getValue()) ? (getValue() as string[]).includes(option) : false}
                  onChange={e => {
                    const currentValue = getValue();
                    const currentArray = Array.isArray(currentValue) ? currentValue : [];
                    const newValue = e.target.checked
                      ? [...currentArray, option]
                      : currentArray.filter((item: string) => item !== option);
                    handleChange(newValue);
                  }}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">{option}</span>
              </label>
            ))}
          </div>
        )}

        {question.type === 'dropdown' && (
          <div className="relative ms-4">
            <select
              value={getValue() as string}
              onChange={e => handleChange(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
            >
              {getOptions().map(option => (
                <option key={option} value={option}>
                  {currencySymbolMap[option] ? currencySymbolMap[option] + " - " : ""}{option}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </div>
          </div>
        )}

        {question.type === 'single-checkbox' && (
          <>
            <div className="space-y-3 ml-5">
              <label className="flex items-start gap-3 p-3 hover:bg-gray-50 cursor-pointer rounded-lg">
                <input
                  type="checkbox"
                  checked={Boolean(getValue())}
                  onChange={e => handleChange(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                {/* Separate description text beside checkbox */}
                <span className="text-sm text-gray-700">
                  {question.checkboxLabel ? question.checkboxLabel : ''}
                </span>
              </label>
            </div>
          </>
        )}


        {question.defaultText && (
          <p className="text-xs text-gray-500 mt-3 ms-4">{question.defaultText}</p>
        )}

        {question.id === mrrConfigQuestions.INCLUDE_TAX && (
          <div className="mt-3 ml-5 p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">
              <strong>Example:</strong> If payment amount is $100 and tax is $10 →{' '}
              {Boolean(getValue()) ? (
                <><strong>MRR = $90 </strong>(tax excluded)</>
              ) : (
                <><strong>MRR = $100 </strong>(tax included)</>
              )}
            </p>
          </div>
        )}
         {question.id === mrrConfigQuestions.TRANSACTION_FEES && (
          <>
            {/* Show fee configuration inputs for Chargebee when checkbox is checked */}
            {activeSourceTab === 'chargebee' && Boolean(getValue()) && (
              <div className="mt-4 ml-16">
                <div className='bg-gray-50 p-4 rounded-xl space-y-4'>
                {/* Sub-question: Chargebee transaction fee */}
                <div className="mb-8">
                  <div className="flex items-center gap-2">
                    <label className="font-medium text-gray-900">
                      Does Chargebee charge you any fees for each transaction?
                    </label>
                    <button
                      type="button"
                      onClick={() => setIsTransactionFeesDrawerOpen(true)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1"
                    >
                      <HelpCircle className="w-4 h-4" />
                      Learn more
                    </button>
                  </div>
                  <div className="mt-2 ml-1 flex items-center gap-6">
                    <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="radio"
                        name="chargebeeChargesPerTxnFee"
                        checked={chargebeeChargesPerTxnFee === true}
                        onChange={() => setChargebeeChargesPerTxnFee(true)}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Yes</span>
                    </label>
                    <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="radio"
                        name="chargebeeChargesPerTxnFee"
                        checked={chargebeeChargesPerTxnFee === false}
                        onChange={() => setChargebeeChargesPerTxnFee(false)}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">No</span>
                    </label>
                  </div>
                </div>

                {chargebeeChargesPerTxnFee && (
                  <div className="ml-6 space-y-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fees by Chargebee for per transaction (%)
                    </label>
                    <div className="relative w-32">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={chargebeeFeePerTransaction}
                        onChange={(e) => setChargebeeFeePerTransaction?.(parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="0.00"
                      />
                      <span className="absolute right-3 top-2.5 text-gray-500">%</span>
                    </div>
                  </div>
                )}

                {/* Payment Gateway Fee (always asked when excluding transaction fees) */}
                <div className="ml-6 space-y-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Gateway Fee Per Transaction (%)
                  </label>
                  <div className="relative w-32">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={paymentGatewayFees}
                      onChange={(e) => setPaymentGatewayFees?.(parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0.00"
                    />
                    <span className="absolute right-3 top-2.5 text-gray-500">%</span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Enter the percentage fee charged by your payment gateway
                  </p>
                </div>

                <div className="mt-8 ml-6 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <strong>Example:</strong> If payment amount is $100, Chargebee fee is {chargebeeChargesPerTxnFee ? chargebeeFeePerTransaction : 0}%, and Payment Gateway fee is {paymentGatewayFees}% →{' '}
                    <strong>
                      MRR = ${(100 - (100 * ((chargebeeChargesPerTxnFee ? chargebeeFeePerTransaction : 0) + paymentGatewayFees) / 100)).toFixed(2)}
                    </strong> (fees excluded)
                  </p>
                </div>
                </div>
              </div>
            )}

            {/* Show generic example for non-Chargebee sources */}
            {activeSourceTab !== 'chargebee' && (
              <div className="mt-3 ml-5 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  <strong>Example:</strong> If payment amount is $100 and transaction fee is $2 →{' '}
                  {Boolean(getValue()) ? (
                    <><strong>MRR = $98 </strong>(tax excluded)</>
                  ) : (
                    <><strong>MRR = $100 </strong>(tax included)</>
                  )}
                </p>
              </div>
            )}
          </>
        )}

        {/* Custom rendering for Chargebee fee configuration - OLD, keeping for backward compatibility but won't show */}
        {question.id === mrrConfigQuestions.CHARGEBEE_FEES && (
          <div className="ml-5 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Chargebee Fee Per Transaction */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Chargebee Fee Per Transaction (%)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={chargebeeFeePerTransaction}
                    onChange={(e) => setChargebeeFeePerTransaction?.(parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                  <span className="absolute right-3 top-2 text-gray-500">%</span>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Enter the percentage fee charged by Chargebee per transaction
                </p>
              </div>

              {/* Payment Gateway Fees */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Gateway Fees (%)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={paymentGatewayFees}
                    onChange={(e) => setPaymentGatewayFees?.(parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                  <span className="absolute right-3 top-2 text-gray-500">%</span>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Enter the percentage fee charged by your payment gateway
                </p>
              </div>
            </div>

            {/* Example Calculation */}
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>Example:</strong> If payment amount is $100, Chargebee fee is {chargebeeFeePerTransaction}%, and Payment Gateway fee is {paymentGatewayFees}% →{' '}
                <strong>
                  MRR = ${(100 - (100 * (chargebeeFeePerTransaction + paymentGatewayFees) / 100)).toFixed(2)}
                </strong> (fees excluded)
              </p>
            </div>
          </div>
        )}
      </div>
    );
  };


  const metricsConfig: MetricConfig[] = [
    {
      id: 'mrr',
      label: 'MRR',
      labelBgColor: '#dbeafe',
      labelTextColor: '#1e40af',
      title: 'MRR Configuration',
      description: 'Configure MRR calculation settings',
      isCompleted: selectedMrrStatuses.length > 0,
      isActive: activeMetric === 'mrr',
      questions: subscriptionQuestions,
      tip: {
        title: "What is Monthly Recurring Revenue?",
        content: (
          <div className="text-sm text-amber-800 flex flex-col gap-2">
            <div className="bg-amber-50 p-3 text-amber-900">
              <span className="font-medium"></span> MRR is the amount of revenue you get from your active customers on a monthly basis. It represents predictable, recurring income from subscriptions or contracts.
            </div>
            <div className="ml-4">
              <strong>Includes:</strong> Recurring fees only<br />
              <strong>Excludes:</strong> Refunds, one-time charges, setup fees, and taxes
            </div>
            <div className="mt-2 ml-4">
              <strong>Formula:</strong><br />
              <span className="bg-white border border-amber-200 rounded px-2 py-1 inline-block mt-1">MRR = Sum of all active subscription fees for the month</span>
            </div>
            <div className="mt-2 ml-4">
              <strong>Why it matters:</strong><br />
              MRR helps you track predictable revenue, forecast growth, and measure financial health month-over-month.
            </div>
          </div>
        ),
        icon: <CheckCircle className="w-4 h-4 text-amber-600 mr-2" />,
        type: "info",
        timeout: 2000
      }
    }
  ];


  const renderMetricContent = (metric: MetricConfig) => {
    const filteredQuestions =
      selectedDataType !== 'hierarchical'
        ? metric.questions.filter
        (q => q.id === mrrConfigQuestions.PAYMENT_STATUS || 
              q.id === mrrConfigQuestions.INCLUDE_TAX || 
              q.id === mrrConfigQuestions.BASE_CURRECY ||
              q.id === mrrConfigQuestions.TRANSACTION_FEES
                                      )
        : metric.questions;

    return (
      <div className="space-y-6">
        {metric.tip && (
          <div className="mb-4">
            <TipsSection
              title={metric.tip.title}
              content={metric.tip.content}
              icon={metric.tip.icon}
              type={metric.tip.type}
              timeout={metric.tip.timeout}
            />
          </div>
        )}

        {filteredQuestions.map((question, index) => {
          const questionWithNumber = { ...question, number: index + 1 };
          return (
            <div key={question.id}>
              {renderQuestionContent(questionWithNumber)}
            </div>
          );
        })}

        <div className="flex justify-end">
          <button
            onClick={() => {
              setContinuedMetrics(prev => new Set([...Array.from(prev), metric.id]));
              setIsMrrCompleted(true)
              setShowCelebration(true)
            }}
            className="flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-colors bg-blue-600 text-white hover:bg-blue-700">
            <span>Save Changes</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  if (!isDemoMode && (isLoadingData || !isReady)) {
    return (
      <div className="w-full mt-5">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Loading configuration options...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full mt-5">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 ms-9">
        {metricsConfig.map((metric, idx) => (
          <div key={metric.id} className={`border-b border-gray-200 last:border-b-0 ${metric.isActive ? 'bg-blue-50' : ''}`}>
            <button
              type="button"
              onClick={() => setActiveMetric(metric.isActive ? '' : metric.id)}
              className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full border-2 bg-blue-500 text-white font-semibold text-sm">
                    {idx + 1}
                  </div>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">{metric.title}</div>
                  <div className="text-sm text-gray-500">{metric.description}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {metric.isCompleted && (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                )}
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${metric.isActive ? 'rotate-180' : ''}`} />
              </div>
            </button>
            {metric.isActive && (
              <div className="border-t border-gray-200 p-6 bg-white">
                {renderMetricContent(metric)}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className='mt-6 ms-9'>
      
      {showCelebration && <FeatureUnlockCelebration
        description="Great job! You have completed the Revenue Analytics Setup. You've unlocked the following features: "
        events={[]}
        features={[
          {
            id: "revenue_analytics_report",
            name: "Revenue Intelligence Report",
            description: "Unlock detailed insights into your Revenue trends and growth.",
            icon: <TrendingUp className="w-5 h-5 text-blue-600" />,
            unlocked: true,
            url: "/revenue-analytics/dashboard"
          }
        ]}
        completed={true}
        hideRecentEvents={true}
        title="Excellent! You've completed basic setup for Revenue Intelligence. We are now analyzing your revenue data and calculating your Revenue metrics."
      />}
      </div>

      {/* Transaction Fees Learn More Drawer */}
      {isTransactionFeesDrawerOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setIsTransactionFeesDrawerOpen(false)} />

          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white">
              <div className="flex items-center gap-3">
                <img src={ChargebeeIcon} alt="Chargebee" className="h-6 w-6" style={{ objectFit: 'contain' }} />
                <h2 className="text-lg font-semibold text-gray-900">Transaction Fees</h2>
              </div>
              <button
                onClick={() => setIsTransactionFeesDrawerOpen(false)}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Visual Flow Graphic */}
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-3">How fees impact your revenue</h3>

              {/* With Chargebee Fee */}
              <div className="mb-4">
                <p className="text-xs text-gray-500 mb-2">With Chargebee fee:</p>
                <div className="flex items-center justify-between text-center">
                  <div className="flex-1">
                    <div className="w-10 h-10 mx-auto bg-white rounded-full flex items-center justify-center border border-gray-200">
                      <span className="text-sm font-bold text-gray-700">$100</span>
                    </div>
                    <p className="text-xs font-medium text-gray-700 mt-1">Gross</p>
                  </div>
                  <div className="text-gray-400 text-xs">→</div>
                  <div className="flex-1">
                    <div className="w-10 h-10 mx-auto bg-white rounded-full flex items-center justify-center border border-gray-200">
                      <img src={ChargebeeIcon} alt="Chargebee" className="w-5 h-5" style={{ objectFit: 'contain' }} />
                    </div>
                    <p className="text-xs font-medium text-gray-700 mt-1">Chargebee</p>
                    <p className="text-xs text-gray-500">-0.75%</p>
                  </div>
                  <div className="text-gray-400 text-xs">→</div>
                  <div className="flex-1">
                    <div className="w-10 h-10 mx-auto bg-white rounded-full flex items-center justify-center border border-gray-200">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg" alt="Stripe" className="w-5 h-5" style={{ objectFit: 'contain' }} />
                    </div>
                    <p className="text-xs font-medium text-gray-700 mt-1">Gateway</p>
                    <p className="text-xs text-gray-500">-2.9%</p>
                  </div>
                  <div className="text-gray-400 text-xs">→</div>
                  <div className="flex-1">
                    <div className="w-10 h-10 mx-auto bg-white rounded-full flex items-center justify-center border border-gray-200">
                      <span className="text-xs font-bold text-gray-700">$96</span>
                    </div>
                    <p className="text-xs font-medium text-gray-700 mt-1">Net MRR</p>
                  </div>
                </div>
              </div>

              {/* Without Chargebee Fee */}
              <div>
                <p className="text-xs text-gray-500 mb-2">Without Chargebee fee:</p>
                <div className="flex items-center justify-between text-center">
                  <div className="flex-1">
                    <div className="w-10 h-10 mx-auto bg-white rounded-full flex items-center justify-center border border-gray-200">
                      <span className="text-sm font-bold text-gray-700">$100</span>
                    </div>
                    <p className="text-xs font-medium text-gray-700 mt-1">Gross</p>
                  </div>
                  <div className="text-gray-400 text-xs">→</div>
                  <div className="flex-1">
                    <div className="w-10 h-10 mx-auto bg-white rounded-full flex items-center justify-center border border-gray-200">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg" alt="Stripe" className="w-5 h-5" style={{ objectFit: 'contain' }} />
                    </div>
                    <p className="text-xs font-medium text-gray-700 mt-1">Gateway</p>
                    <p className="text-xs text-gray-500">-2.9%</p>
                  </div>
                  <div className="text-gray-400 text-xs">→</div>
                  <div className="flex-1">
                    <div className="w-10 h-10 mx-auto bg-white rounded-full flex items-center justify-center border border-gray-200">
                      <span className="text-xs font-bold text-gray-700">$96.80</span>
                    </div>
                    <p className="text-xs font-medium text-gray-700 mt-1">Net MRR</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4 text-sm text-gray-700">

              {/* Why this matters */}
              <section>
                <h3 className="font-semibold text-gray-900 mb-2">Why does this matter?</h3>
                <p className="text-xs text-gray-600">
                  Transaction fees reduce your actual revenue. Excluding them from MRR calculations gives you a more accurate view of what you actually retain — helping you make better financial decisions and forecasts.
                </p>
              </section>

              <hr className="border-gray-200" />

              {/* Chargebee Fee */}
              <section>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-gray-900">Chargebee Fee</h3>
                  <a href="https://www.chargebee.com/pricing/" target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
                    Pricing <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <p className="text-xs text-gray-600 mb-2">Revenue-based pricing, not per-transaction.</p>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="bg-gray-50 rounded p-2 text-center">
                    <p className="font-medium">Starter</p>
                    <p className="text-gray-500">Free to $250K</p>
                  </div>
                  <div className="bg-gray-50 rounded p-2 text-center">
                    <p className="font-medium">Performance</p>
                    <p className="text-gray-500">$599/mo</p>
                  </div>
                  <div className="bg-gray-50 rounded p-2 text-center">
                    <p className="font-medium">Enterprise</p>
                    <p className="text-gray-500">Custom</p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">Overage: 0.75% beyond plan limits</p>
              </section>

              <hr className="border-gray-200" />

              {/* Payment Gateway Fee */}
              <section>
                <h3 className="font-semibold text-gray-900 mb-2">Payment Gateway Fee</h3>
                <p className="text-xs text-gray-600 mb-2">Per-transaction fees charged by payment processors.</p>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-1.5 font-medium">Gateway</th>
                      <th className="text-left py-1.5 font-medium">Card</th>
                      <th className="text-left py-1.5 font-medium">ACH</th>
                      <th className="py-1.5"></th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-600">
                    <tr className="border-b border-gray-100">
                      <td className="py-1.5">Stripe</td>
                      <td className="py-1.5">2.9% + $0.30</td>
                      <td className="py-1.5">0.8%</td>
                      <td className="py-1.5"><a href="https://stripe.com/pricing" target="_blank" rel="noopener noreferrer" className="text-blue-600"><ExternalLink className="w-3 h-3" /></a></td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-1.5">Braintree</td>
                      <td className="py-1.5">2.9% + $0.30</td>
                      <td className="py-1.5">0.75%</td>
                      <td className="py-1.5"><a href="https://www.braintreepayments.com/learn/credit-card-processing-fees" target="_blank" rel="noopener noreferrer" className="text-blue-600"><ExternalLink className="w-3 h-3" /></a></td>
                    </tr>
                    <tr>
                      <td className="py-1.5">PayPal</td>
                      <td className="py-1.5">2.9% + $0.30</td>
                      <td className="py-1.5">Varies</td>
                      <td className="py-1.5"><a href="https://www.paypal.com/us/webapps/mpp/merchant-fees" target="_blank" rel="noopener noreferrer" className="text-blue-600"><ExternalLink className="w-3 h-3" /></a></td>
                    </tr>
                  </tbody>
                </table>
                <p className="text-xs text-gray-500 mt-2">+1% for international transactions, +1% for currency conversion</p>
                <p className="text-xs text-gray-500 mt-1 italic">* For reference only. Please check your account for actual fees.</p>
              </section>

              <hr className="border-gray-200" />

              {/* Why exclude fees */}
              <section>
                <h3 className="font-semibold text-gray-900 mb-2">Why Exclude Fees from MRR?</h3>
                <p className="text-xs text-gray-600 mb-3">
                  Excluding fees gives a more accurate view of net revenue you actually retain.
                </p>
                <div className="bg-gray-50 rounded p-3 text-xs font-mono">
                  <div className="space-y-1 text-gray-600">
                    <div className="flex justify-between"><span>Gross Payment</span><span>$100.00</span></div>
                    <div className="flex justify-between"><span>− Chargebee (0.75%)</span><span className="text-red-600">-$0.75</span></div>
                    <div className="flex justify-between"><span>− Stripe (2.9% + $0.30)</span><span className="text-red-600">-$3.20</span></div>
                    <div className="flex justify-between border-t border-gray-300 pt-1 mt-1 font-semibold text-gray-900"><span>MRR</span><span className="text-green-600">$96.05</span></div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SetupMRR;
